import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Icon, TableCell, TableRow, Button, Select, SelectItem } from "@tremor/react";
import PropTypes from "prop-types";
import { Spinner } from "../../ui";
import { TableComponent } from "../components";
import { ChatBubbleBottomCenterIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../../hooks";
import { getConversationsFromAPI, getTokenFromAPI, clearUnreadConversation } from "../../store/chats";
import { MenuLayout } from "../layouts";

const headers = [
  "Número de Factura",
  "Mensajes Sin Leer",
  "Fecha",
  "Cliente MX",
  "Cliente USD",
  "Último Mensaje",
  "Fecha de Último Mensaje",
  "Acciones",
];

const getLastMessageDate = (dateString) => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // Los meses van de 0 a 11, así que sumamos 1
  const day = date.getUTCDate();
  const min = date.getMinutes();
  const hour = date.getHours();
  const dateModification = `${year}-${String(month).padStart(2, "0")}-${String(
    day
  ).padStart(2, "0")} ${hour}:${min}`;
  return dateModification;
};

const getInfoConversation = (conversation) => {
  if (!conversation) return "Sin Mensajes";
  const { messagesUnread } = conversation;
  if (messagesUnread === 0 || messagesUnread === null)
    return "Sin Mensajes Nuevos";
  return `${messagesUnread} mensajes nuevos`;
};

const RowTable = ({
  id,
  client,
  buyer,
  creationDate,
  conversation,
  invoiceNumber = 0,
  navigate,
  getHook,
  isUnread = false,
}) => {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const date = new Date(creationDate);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // Los meses van de 0 a 11, así que sumamos 1
  const day = date.getUTCDate();
  const dateModification = `${year}-${String(month).padStart(2, "0")}-${String(
    day
  ).padStart(2, "0")}`;

  const goToChat = async () => {
    setIsLoaded(true);
    const { data, isSuccess } = await getHook(
      `/chat/get-token-conversation/${id}`
    );
    setIsLoaded(false);
    if (!isSuccess) return;
    if (conversation?.tokenConversation) {
      dispatch(clearUnreadConversation(conversation.tokenConversation));
    }
    navigate(`/admin/chat/${id}/${data.data}`);
  };

  return (
    <TableRow className={isUnread ? "bg-blue-950/40 border-l-4 border-blue-500 font-semibold" : ""}>
      <TableCell>{invoiceNumber}</TableCell>
      <TableCell>
        {conversation ? getInfoConversation(conversation) : "Sin Mensajes"}
      </TableCell>
      <TableCell>{dateModification}</TableCell>
      <TableCell>{client.name}</TableCell>
      <TableCell>{buyer.name}</TableCell>
      {conversation ? (
        <>
          <TableCell>
            {conversation.lastMessage
              ? conversation.lastMessage.message
              : "Sin Mensajes"}
          </TableCell>
          <TableCell>
            {conversation.lastMessage
              ? getLastMessageDate(conversation.lastMessage.date)
              : "Sin Mensajes"}
          </TableCell>
        </>
      ) : (
        <>
          <TableCell>Sin Mensajes</TableCell>
          <TableCell>Sin Mensajes</TableCell>
        </>
      )}
      <TableCell>
        {isLoaded ? (
          <p>Cargando...</p>
        ) : (
          <Icon
            variant="solid"
            icon={ChatBubbleBottomCenterIcon}
            onClick={goToChat}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

export const ConversationsPage = () => {
  const { get } = useAPI();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { chats, pagination, isLoading, hasError, errorMessage, unreadConversationSids } = useSelector(
    (state) => state.chats
  );

  useEffect(() => {
    dispatch(getConversationsFromAPI(1, 10));
    dispatch(getTokenFromAPI());
  }, []);

  const handlePageChange = (newPage) => {
    dispatch(getConversationsFromAPI(newPage, pagination?.limit || 10));
  };

  const handleLimitChange = (newLimit) => {
    dispatch(getConversationsFromAPI(1, parseInt(newLimit, 10)));
  };

  if (isLoading && !hasError) {
    return <Spinner />;
  }

  if (chats.length === 0 && !hasError) {
    return <Spinner />;
  }

  const tableContent = chats.map((chat) => (
    <RowTable
      key={chat.id}
      id={chat.id}
      client={chat.client}
      buyer={chat.buyer}
      creationDate={chat.creationDate}
      invoiceNumber={chat.invoiceNumber}
      conversation={chat.conversation}
      navigate={navigate}
      getHook={get}
      isUnread={unreadConversationSids?.includes(chat.conversation?.tokenConversation)}
    />
  ));
  return (
    <MenuLayout hasError={hasError} messageError={errorMessage}>
      <TableComponent
        tableHeaders={headers}
        tableContent={tableContent}
        title="Conversaciones de los Servicios"
      />
      {pagination && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Mostrar</span>
            <div className="w-24">
              <Select value={String(pagination.limit)} onValueChange={handleLimitChange}>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </Select>
            </div>
            <span className="text-sm text-gray-500">por página</span>
          </div>

          <div className="text-sm text-gray-600">
            Página <strong>{pagination.page}</strong> de <strong>{pagination.totalPages}</strong> ({pagination.total} conversaciones)
          </div>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="primary"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </MenuLayout>
  );
};

RowTable.propTypes = {
  buyer: PropTypes.object.isRequired,
  client: PropTypes.object.isRequired,
  conversation: PropTypes.object,
  creationDate: PropTypes.string.isRequired,
  getHook: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  navigate: PropTypes.func.isRequired,
  invoiceNumber: PropTypes.number,
  isUnread: PropTypes.bool,
};
